import './styles.scss';

import React, { useMemo, useState } from 'react';

import { Button } from 'antd';
import { useSearchParams } from 'react-router-dom';
import TMIcon from 'tm-icons-library';

import DynamicWrapper from '../../components/DynamicWrapper/DynamicWrapper';
import DynamicHeader from '../../components/Header/DynamicHeader';
import { useTheme } from '../../context/ThemeProvider';
import { REFRESH_TOKEN_KEY } from '../../shared/data';
import { useAppSelector } from '../../shared/hooks';
import { CustomApp } from '../../types/HostMetadata';
import { HomeRoutes } from '../../types/routes';
import { Onboarding, UAMPermissions } from '../../types/userTypes';
import NoDataFound from '../Feed/components/NoDataFound/NoDataFound';
import CustomAppsLoader from './CustomAppsLoader';

interface Props {
  app: CustomApp;
  name: HomeRoutes;
}

const CustomAppPreview: React.FC<Props> = ({ app, name }) => {
  const [searchParams] = useSearchParams();

  const { colors: COLORS } = useTheme();

  const {
    hostMetadata: { host },
    customRouter: { homeRoutes },
    mDeeplinkUrl,
  } = useAppSelector((state) => state.app);

  const {
    subscribedMangoes,
    id: userId,
    type: userType,
    userAccessPermissions,
  } = useAppSelector((state) => state.user);

  const [isCustomAppReady, setIsCustomAppReady] = useState(false);

  const {
    checkAllowedMangoes,
    disabledIconName,
    disabledTitle,
    disabledSubtitle,
    disabledButtonText,
    disabledButtonLink,
    disabledButtonTarget,
  } = app?.additionalConfig || {};

  const hasAccess = useMemo(() => {
    // if both app and customUrl are not provided, return false (don't have access)
    if (!app) return false;

    // creator or UAM with custom app access has access to all custom apps
    if (
      userType === Onboarding.CREATOR_COMPLETED ||
      (userType === Onboarding.CREATOR_RESTRICTED &&
        userAccessPermissions?.includes(UAMPermissions.CUSTOM_APP_ACCESS))
    ) {
      return true;
    }

    // if allowedMangoes is not set, then the app is accessible to all users
    if (!app?.allowedMangoes) {
      return true;
    }

    // if checkAllowedMangoes is not set, then the app is accessible to all users
    if (!checkAllowedMangoes) {
      return true;
    }

    const { allowedMangoes } = app;

    // if the app has allowedMangoes, check if the user has access to any of them
    return subscribedMangoes.some((m) => allowedMangoes.includes(m._id));
  }, [
    app,
    checkAllowedMangoes,
    subscribedMangoes,
    userAccessPermissions,
    userType,
  ]);

  const appUrl = useMemo(() => {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);

    let url = '';

    if (app.domain) {
      url += app.domain;
    } else {
      url += `${mDeeplinkUrl}web/apps`;
    }

    if (app.slug) {
      if (!url.endsWith('/')) {
        url += '/';
      }
      url += `${app.slug}`;
    }

    if (app.entryPath && app.entryPath.length > 0) {
      if (!url.endsWith('/')) {
        url += '/';
      }
      url += `${app.entryPath}`;
    }

    const initialQueryParams = app?.initialQueryParams || {};

    // delete host from initialQueryParams as it will be passed as a query param from the parent where it's embedded
    delete initialQueryParams.host;

    const appQueryParamsToPass = new URLSearchParams({
      host,
      ...(userId ? { userId } : {}),
      ...initialQueryParams,
      ...Object.fromEntries(searchParams.entries()),
      ...(refreshToken ? { refreshToken } : {}),
    });

    // if (refreshToken) {
    if (url.match(/(\?|&)([^=]+)=([^&]+)/g)) {
      url += '&';
    } else {
      url += '?';
    }

    // url += `refreshToken=${refreshToken}&userId=${userId}`;
    // }

    // if (app.initialQueryParams) {
    //   url += `${url.includes('?') ? '&' : '?'}${objToQueryParams({
    //     ...app?.initialQueryParams,
    //   })}`;
    // }

    if (appQueryParamsToPass.size) {
      // url += `${url.includes('?') ? '&' : '?'}${appQueryParamsToPass.toString()}`;
      url += appQueryParamsToPass.toString();
    }

    return url;
  }, [app, mDeeplinkUrl, userId, host, searchParams]);

  if (app.target === '_blank') {
    window.open(appUrl, '_blank');

    // open the first home route if the default route is opened in a new tab
    window.location.href = homeRoutes[0].name;

    return null;
  }

  return (
    <DynamicWrapper path={name}>
      <div className="pageRoot">
        <DynamicHeader path={name} title={app.name} />

        <div className="pageContent custom-app-preview__content">
          {!isCustomAppReady && hasAccess ? <CustomAppsLoader /> : null}

          {hasAccess ? (
            <iframe
              onLoad={() => setIsCustomAppReady(true)}
              allow="microphone; clipboard-write"
              sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-downloads"
              className="app-iframe"
              title={app?.name || 'Custom App'}
              src={appUrl}
              style={{ width: '100%', height: '100%' }}
            />
          ) : (
            <div className="not-allowed__content">
              <NoDataFound
                iconElement={
                  <TMIcon
                    name={disabledIconName || 'warning'}
                    size={64}
                    color={COLORS.ICON}
                  />
                }
                title={disabledTitle || 'Feature Locked!'}
                subtitle={disabledSubtitle}
                extra={
                  disabledButtonLink ? (
                    <Button
                      className="siteBtn siteBtnPrimary"
                      style={{ marginTop: 16, marginInline: 'auto' }}
                      onClick={() => {
                        if (disabledButtonLink) {
                          window.open(
                            disabledButtonLink,
                            disabledButtonTarget || '_self',
                          );
                        }
                      }}>
                      {disabledButtonText || 'Unlock Now'}
                    </Button>
                  ) : undefined
                }
                style={{
                  width: '100%',
                }}
              />
            </div>
          )}
        </div>
      </div>
    </DynamicWrapper>
  );
};

export default React.memo(CustomAppPreview);
